import dayjs from 'dayjs';
import { type Customer, CustomerOrder, Site, type SiteMenuOrder } from '../../../../entities';
import CustomerOrderRevision from '../../../../entities/customer-order-revision';
import { LocalDate } from '../../../../utils/local-date';
import { getEarliestOrderDate } from '../../../../utils/order-utils';
import utils, {
  sortSiteMenuOrdersByRouteThenMealCategorySortOrder,
  sortSitesByCountyAndName,
} from '../../../../utils/utils';

const timeZone = process.env.REACT_APP_TIMEZONE;

/** A site menu order being edited. "temporaryId" identifies it until the order is saved */
export type EditableSiteMenuOrder = SiteMenuOrder & { temporaryId: string };

/** The site menu orders of the order being edited, by site id */
export type SiteMenuOrdersBySite = Record<string, EditableSiteMenuOrder[]>;

/** "siteMenuOrders" sorted by route then meal category, without changing "siteMenuOrders" */
export const sortSiteMenuOrders = (
  siteMenuOrders: EditableSiteMenuOrder[]
): EditableSiteMenuOrder[] =>
  sortSiteMenuOrdersByRouteThenMealCategorySortOrder([
    ...siteMenuOrders,
  ]) as EditableSiteMenuOrder[];

const isValidDate = (date: LocalDate, customer: Customer): boolean => {
  const earliestValidOrderDate = utils
    .addBusinessDays(
      dayjs().tz(timeZone),
      customer.customerSetting.dueDaysToPlaceOrders,
      customer.customerSetting.dueTimeToPlaceOrders
    )
    .startOf('d');
  return date.isSameOrAfter(earliestValidOrderDate);
};

/**
 * The date the form starts with: the order's own date, else the date asked for in the URL when the
 * customer can still order for it, else the earliest date the customer can order for
 */
export const getInitialOrderDate = (
  customerOrder: CustomerOrder,
  customer: Customer,
  preSelectedOrderDate: string | null
): LocalDate => {
  if (customerOrder.orderDate) {
    return customerOrder.orderDate;
  }
  // A missing date is null, which makes an invalid date: never undefined, which would mean today
  const requestedDate = new LocalDate(preSelectedOrderDate);
  return isValidDate(requestedDate, customer)
    ? requestedDate
    : new LocalDate(getEarliestOrderDate(customer.customerSetting));
};

/**
 * The sites of "customer" that can be ordered for, sorted by county then name: the active ones, plus
 * the inactive ones which already have meals on the order being updated
 */
export const getOrderSites = (
  customer: Customer | undefined,
  customerOrder: CustomerOrder
): Site[] => {
  const isExistingOrder = !!customerOrder.id;
  const sitesWithMealsOnThisOrder = new Set(
    customerOrder.siteMenuOrders.flatMap((smo) => (smo.site?.id ? [smo.site.id] : []))
  );
  return sortSitesByCountyAndName(customer?.sites ?? []).filter(
    (site) => site.isActive || (isExistingOrder && sitesWithMealsOnThisOrder.has(site.id!))
  );
};

/** The site menu orders "customerOrder" starts with for each of "sites" */
export const getInitialSiteMenuOrders = (
  sites: Site[],
  customerOrder: CustomerOrder
): SiteMenuOrdersBySite =>
  Object.fromEntries(
    sites.map((site) => [
      site.id!,
      sortSiteMenuOrders(
        customerOrder.siteMenuOrders
          .filter((smo) => smo.site?.id === site.id)
          .map((smo) => ({ ...smo, temporaryId: utils.randomUUID() }))
      ),
    ])
  );

/** "sites", each carrying its site menu orders in "siteMenuOrders", as the summary and the
 * request expect them
 */
export const withSiteMenuOrders = (
  sites: Site[],
  siteMenuOrdersBySite: SiteMenuOrdersBySite
): Site[] =>
  sites.map((site) =>
    Object.assign(new Site(), site, { siteMenuOrders: siteMenuOrdersBySite[site.id!] ?? [] })
  );

const toCartSite = (site: Site): Partial<Site> => ({
  id: site.id,
  name: site.name,
  custSiteNumber: site.custSiteNumber,
  siteNumber: site.siteNumber,
});

/**
 * The order kept in the cart, so a new order isn't lost when leaving the page: only what's needed to
 * restore it, without the references between entities
 */
export const toCartCustomerOrder = (
  customerId: string,
  orderDate: LocalDate,
  siteMenuOrders: EditableSiteMenuOrder[]
): CustomerOrder =>
  Object.assign(new CustomerOrder(), {
    customer: { id: customerId },
    orderDate,
    latestRevision: Object.assign(new CustomerOrderRevision(), {
      siteMenuOrders: siteMenuOrders.map((smo) => ({
        ...smo,
        site: toCartSite(smo.site),
        route: { ...smo.route, site: undefined },
        scheduledMeal: {
          ...smo.scheduledMeal,
          site: undefined,
          mealCategory: {
            id: smo.scheduledMeal.mealCategory.id,
            name: smo.scheduledMeal.mealCategory.name,
          },
        },
        siteMenuBeverageOrders: smo.siteMenuBeverageOrders?.map((smbo) => ({
          ...smbo,
          site: toCartSite(smbo.site),
          route: { ...smbo.route, site: undefined },
          beverage: { ...smbo.beverage, site: undefined },
          siteMenuOrder: undefined,
        })),
        siteMenuSupplyOrders: smo.siteMenuSupplyOrders?.map((smso) => ({
          ...smso,
          site: toCartSite(smso.site),
          route: { ...smso.route, site: undefined },
          supply: { ...smso.supply, site: undefined },
          siteMenuOrder: undefined,
        })),
        temporaryId: undefined,
      })),
    }),
  });
